/**
 * Turning compose form state into the shape `sendJobEmail` wants.
 *
 * This lives outside the component because getting it wrong crashes the send
 * button at runtime while typechecking and linting cleanly — react-hook-form
 * builds its submit payload from the fields that are *currently registered*,
 * so a field whose row has never been revealed is simply absent from the data,
 * regardless of what `defaultValues` says.
 */

/**
 * What react-hook-form actually hands `onSubmit` for the compose form.
 *
 * `cc` and `bcc` are optional because their rows only mount when the Cc / Bcc
 * toggles are on: on an untouched compose those keys do not exist at all. The
 * optionality is not cosmetic — it is what makes the compiler insist on a
 * guard at every use.
 */
export interface ComposeFormValues {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  forwardRepliesToEmail?: string[];
}

/**
 * What a compose surface should open with.
 *
 * Lives here rather than beside either surface because both the dialog and the
 * inline reply composer are built from it, and the panel that decides a reply's
 * recipient must be the only thing that decides its subject and its threading
 * parent too — see `replyTarget`.
 */
export interface ComposeIntent {
  title?: string;
  defaultTo?: string;
  defaultSubject?: string;
  /** Prefilled body as HTML, from the organization's template. */
  defaultBodyHtml?: string;
  /**
   * Set by Reply only. Forward deliberately leaves it unset: a forward starts a
   * new conversation for a new recipient, and threading it onto the original
   * would drop it into a thread they were never part of.
   */
  inReplyToId?: string;
}

/** Recipient lists the API expects: present when non-empty, otherwise omitted. */
export function optionalRecipients(values?: string[]): string[] | undefined {
  return values && values.length > 0 ? values : undefined;
}

export interface ComposeRecipients {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  forwardRepliesToEmail?: string[];
}

/**
 * Normalises every recipient field in one place, so a newly added field cannot
 * be read directly and reintroduce the missing-key crash.
 */
export function buildRecipients(data: ComposeFormValues): ComposeRecipients {
  return {
    to: optionalRecipients(data.to),
    cc: optionalRecipients(data.cc),
    bcc: optionalRecipients(data.bcc),
    forwardRepliesToEmail: optionalRecipients(data.forwardRepliesToEmail),
  };
}

/**
 * Fields the API reads with `JSON.parse`, so they go over the wire as JSON
 * strings rather than raw values.
 */
const JSON_ENCODED_FIELDS = [
  'bcc',
  'cc',
  'draftJsObjectBody',
  'forwardRepliesToEmail',
  'subject',
  'to',
];

/**
 * Turns the compose payload into the multipart body `POST /api/v1/email/:jobId`
 * expects.
 *
 * Extracted from the api layer for the same reason as the rest of this module:
 * every value here is stringified on its way into FormData, so a field encoded
 * the wrong way typechecks perfectly and fails only against the live API.
 *
 * Two encodings, and mixing them up is the whole hazard:
 * - `JSON_ENCODED_FIELDS` are JSON.parse'd server-side, so they are stringified.
 * - `inReplyToId` is read as a plain UUID and validated with `@IsUUID`, so
 *   quoting it would fail validation.
 *
 * Absent values are omitted rather than appended. `FormData.append` stringifies
 * whatever it is given, so appending an undefined `inReplyToId` would send the
 * literal text "undefined" and 400 every fresh compose.
 */
export function buildSendEmailFormData(
  data: Record<string, unknown>,
  formData: FormData = new FormData()
): FormData {
  Object.keys(data).forEach((key) => {
    const value = data[key];

    if (key === 'attachments') {
      (value as File[] | undefined)?.forEach((file) =>
        formData.append('attachments', file)
      );
    } else if (key === 'attachmentsIds') {
      (value as string[] | undefined)?.forEach((id) =>
        formData.append('attachmentsIds', id)
      );
    } else if (key === 'inReplyToId') {
      if (value) formData.append('inReplyToId', String(value));
    } else if (JSON_ENCODED_FIELDS.includes(key) && value) {
      formData.append(key, JSON.stringify(value));
    }
  });

  return formData;
}
